import { useState, useEffect } from 'react';
import { getISTDateString } from '../lib/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getExpenses, recordExpense, updateExpense, deleteExpense, exportExpensesReport, getPaymentCategories, updatePaymentCategories } from '../lib/api';
import { formatDate } from '../lib/dateUtils';
import { toast } from 'sonner';
import { Receipt, Plus, Wallet, CreditCard, TrendingDown, Pencil, Trash2, Save, X, Download, CalendarDays, Calendar, History, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Default payment categories (used until the org's saved list loads)
const DEFAULT_PAYMENT_CATEGORIES = [
  'PERSONAL WITHDRAWAL',
  'KITCHEN PAYMENTS',
  'HOUSEKEEPING PAYMENTS',
  'REPAIR & MAINTENANCE PAYMENTS',
  'GUEST DEPOSIT RETURN PAYMENTS',
  'SALARY PAYMENTS',
  'ELECTRICITY PAYMENTS',
  'ADMIN PAYMENTS',
  'LAUNDRY PAYMENTS',
  'INTEREST PAYMENTS',
  'RENT PAYMENTS',
  'RECHARGE PAYMENTS',
  'ADVERTISEMENT PAYMENTS'
];

export const Expenses = () => {
  const { canAdd, canEdit, canDelete, canExport } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState(DEFAULT_PAYMENT_CATEGORIES);
  const [manageCatOpen, setManageCatOpen] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [savingCats, setSavingCats] = useState(false);
  
  // View mode and date filters
  const [viewMode, setViewMode] = useState('monthly');
  const [selectedDate, setSelectedDate] = useState(getISTDateString());
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  
  const [form, setForm] = useState({
    date: getISTDateString(),
    category: '',
    description: '',
    amount: 0,
    payment_mode: 'cash'
  });

  const [editForm, setEditForm] = useState({
    date: '',
    category: '',
    description: '',
    amount: 0,
    payment_mode: 'cash'
  });

  // Export handler
  const handleExport = async () => {
    try {
      setExporting(true);
      const params = viewMode === 'daily' ? { date: selectedDate } : 
                     viewMode === 'monthly' ? { month: selectedMonth } : 
                     { lifetime: true };
      const response = await exportExpensesReport(params);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      const filename = viewMode === 'daily' ? `Payments_${selectedDate}` :
                       viewMode === 'monthly' ? `Payments_${selectedMonth}` :
                       'Payments_Lifetime';
      a.download = `${filename}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Payments Report downloaded successfully');
    } catch (err) {
      toast.error('Failed to export Payments Report');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [viewMode, selectedDate, selectedMonth]);

  useEffect(() => {
    getPaymentCategories().then(res => setCategories(res.data.categories || DEFAULT_PAYMENT_CATEGORIES)).catch(() => {});
  }, []);

  const handleAddCategory = async () => {
    const name = newCat.trim().toUpperCase();
    if (!name) return;
    if (categories.some(c => c.toUpperCase() === name)) { toast.error('Category already exists'); return; }
    try {
      setSavingCats(true);
      const res = await updatePaymentCategories([...categories, name]);
      setCategories(res.data.categories || []);
      setNewCat('');
      toast.success('Category added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add category'); }
    finally { setSavingCats(false); }
  };

  const handleRemoveCategory = async (name) => {
    try {
      setSavingCats(true);
      const res = await updatePaymentCategories(categories.filter(c => c !== name));
      setCategories(res.data.categories || []);
      if (form.category === name) setForm(f => ({ ...f, category: '' }));
      toast.success('Category removed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove category'); }
    finally { setSavingCats(false); }
  };

  const fetchData = async () => {
    try {
      const params = viewMode === 'daily' ? { date: selectedDate } : 
                     viewMode === 'monthly' ? { month: selectedMonth } : 
                     { lifetime: true };
      const res = await getExpenses(params);
      // Sort expenses by date (most recent first)
      const sortedExpenses = res.data.sort((a, b) => 
        new Date(b.date) - new Date(a.date)
      );
      setExpenses(sortedExpenses);
    } catch (err) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await recordExpense(form);
      toast.success('Expense recorded');
      setDialogOpen(false);
      setForm({
        date: getISTDateString(),
        category: '',
        description: '',
        amount: 0,
        payment_mode: 'cash'
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleEditClick = (expense) => {
    setEditingExpense(expense);
    setEditForm({
      date: expense.date,
      category: expense.category,
      description: expense.description,
      amount: expense.amount,
      payment_mode: expense.payment_mode
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateExpense(editingExpense.id, editForm);
      toast.success('Expense updated');
      setEditDialogOpen(false);
      setEditingExpense(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update payment');
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    try {
      await deleteExpense(expenseId);
      toast.success('Expense deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete payment');
    }
  };

  const totals = {
    cash: expenses.filter(e => e.payment_mode === 'cash').reduce((sum, e) => sum + e.amount, 0),
    bank: expenses.filter(e => e.payment_mode === 'bank').reduce((sum, e) => sum + e.amount, 0),
    total: expenses.reduce((sum, e) => sum + e.amount, 0)
  };

  const visibleExpenses = expenses.filter(e => {
    const matchesCat = categoryFilter === 'all' || e.category === categoryFilter;
    const q = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || (e.category || '').toLowerCase().includes(q) || (e.description || '').toLowerCase().includes(q);
    return matchesCat && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Payments</h1>
          <p className="text-slate-500 mt-1">Track and manage payments</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={handleExport} 
            disabled={exporting}
            className="!bg-emerald-600 hover:!bg-emerald-700 text-white"
            data-testid="export-expenses-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exporting...' : 'Download Excel'}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            {canAdd('payments') && (
              <DialogTrigger asChild>
                <Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-expense-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Record Payment
                </Button>
              </DialogTrigger>
            )}
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Payment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    required
                    data-testid="expense-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Category</Label>
                    <button type="button" onClick={() => setManageCatOpen(v => !v)} className="text-xs text-blue-700 hover:underline" data-testid="manage-payment-categories-toggle">
                      {manageCatOpen ? 'Done' : 'Manage'}
                    </button>
                  </div>
                  <Select value={form.category} onValueChange={(val) => setForm({ ...form, category: val })}>
                    <SelectTrigger data-testid="expense-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {manageCatOpen && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="manage-payment-categories-panel">
                      <div className="flex gap-2">
                        <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" data-testid="new-payment-category-input" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); } }} />
                        <Button type="button" variant="outline" onClick={handleAddCategory} disabled={savingCats} data-testid="add-payment-category-btn">Add</Button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {categories.map(c => (
                          <span key={c} className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-2.5 py-1 text-xs">
                            {c}
                            <button type="button" onClick={() => handleRemoveCategory(c)} className="text-slate-400 hover:text-rose-600" data-testid={`remove-payment-category-${c}`}>×</button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value.toUpperCase() })}
                  placeholder="BRIEF DESCRIPTION"
                  required
                  data-testid="expense-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })}
                    required
                    data-testid="expense-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Mode</Label>
                  <div className="flex gap-2">
                    <label className={`flex-1 flex items-center justify-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${form.payment_mode === 'cash' ? 'border-rose-500 bg-rose-50' : 'border-slate-200'}`}>
                      <input type="radio" name="mode" value="cash" checked={form.payment_mode === 'cash'} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} className="sr-only" />
                      <Wallet className="h-4 w-4" />
                      <span className="text-sm">Cash</span>
                    </label>
                    <label className={`flex-1 flex items-center justify-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${form.payment_mode === 'bank' ? 'border-rose-500 bg-rose-50' : 'border-slate-200'}`}>
                      <input type="radio" name="mode" value="bank" checked={form.payment_mode === 'bank'} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} className="sr-only" />
                      <CreditCard className="h-4 w-4" />
                      <span className="text-sm">Bank</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1 bg-rose-600 hover:bg-rose-700" data-testid="save-expense-btn">Record Payment</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Edit Payment Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={editForm.date}
                  onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={editForm.category} onValueChange={(val) => setEditForm({ ...editForm, category: val })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value.toUpperCase() })}
                placeholder="BRIEF DESCRIPTION"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Mode</Label>
                <div className="flex gap-2">
                  <label className={`flex-1 flex items-center justify-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${editForm.payment_mode === 'cash' ? 'border-rose-500 bg-rose-50' : 'border-slate-200'}`}>
                    <input type="radio" name="editmode" value="cash" checked={editForm.payment_mode === 'cash'} onChange={(e) => setEditForm({ ...editForm, payment_mode: e.target.value })} className="sr-only" />
                    <Wallet className="h-4 w-4" />
                    <span className="text-sm">Cash</span>
                  </label>
                  <label className={`flex-1 flex items-center justify-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${editForm.payment_mode === 'bank' ? 'border-rose-500 bg-rose-50' : 'border-slate-200'}`}>
                    <input type="radio" name="editmode" value="bank" checked={editForm.payment_mode === 'bank'} onChange={(e) => setEditForm({ ...editForm, payment_mode: e.target.value })} className="sr-only" />
                    <CreditCard className="h-4 w-4" />
                    <span className="text-sm">Bank</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                <Save className="h-4 w-4 mr-1" /> Update
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Mode Tabs */}
      <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="daily" className="flex items-center gap-2" data-testid="daily-tab">
            <CalendarDays className="h-4 w-4" />
            Daily
          </TabsTrigger>
          <TabsTrigger value="monthly" className="flex items-center gap-2" data-testid="monthly-tab">
            <Calendar className="h-4 w-4" />
            Monthly
          </TabsTrigger>
          <TabsTrigger value="lifetime" className="flex items-center gap-2" data-testid="lifetime-tab">
            <History className="h-4 w-4" />
            Lifetime
          </TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="mt-4">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-[200px]"
            data-testid="expense-date-filter"
          />
        </TabsContent>

        <TabsContent value="monthly" className="mt-4">
          <Input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-[200px]"
            data-testid="expense-month-filter"
          />
        </TabsContent>

        <TabsContent value="lifetime" className="mt-4">
          <p className="text-sm text-slate-500">Showing all payments</p>
        </TabsContent>
      </Tabs>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-rose-200" style={{ animationDelay: '0ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 opacity-10"></div>
          <div className="flex items-center gap-1.5 mb-1"><Wallet className="h-4 w-4 text-rose-600" /><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Cash Payments</span></div>
          <p className="text-xl sm:text-2xl font-bold font-mono text-rose-700">₹{totals.cash.toLocaleString()}</p>
        </div>
        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-blue-200" style={{ animationDelay: '60ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 opacity-10"></div>
          <div className="flex items-center gap-1.5 mb-1"><CreditCard className="h-4 w-4 text-blue-600" /><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Bank Payments</span></div>
          <p className="text-xl sm:text-2xl font-bold font-mono text-blue-700">₹{totals.bank.toLocaleString()}</p>
        </div>
        <div className="dash-animate relative overflow-hidden rounded-xl bg-slate-900 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" style={{ animationDelay: '120ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 opacity-20"></div>
          <div className="flex items-center gap-1.5 mb-1"><TrendingDown className="h-4 w-4 text-slate-400" /><span className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">Total Payments</span></div>
          <p className="text-xl sm:text-2xl font-bold font-mono text-white">₹{totals.total.toLocaleString()}</p>
        </div>
      </div>

      {/* Search + Category filter */}
      <div className="flex flex-wrap gap-3">
        <div className="relative w-full sm:w-[280px]">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search category or description..." className="pl-9" data-testid="payment-search-input" />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-[260px]" data-testid="payment-category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Payments Table */}
      <Card className="dash-animate relative overflow-hidden" data-testid="expenses-table" style={{ animationDelay: '180ms' }}>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-500 to-pink-600 z-10" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Payment Records
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-14">
                    <div className="w-16 h-16 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
                      <Receipt className="w-8 h-8 text-rose-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-1">No payments recorded</h3>
                    <p className="text-slate-500 mb-5 text-sm">Record an expense to start tracking your outgoings.</p>
                    {canAdd('payments') && (
                      <Button onClick={() => setDialogOpen(true)} className="bg-blue-800 hover:bg-blue-900" data-testid="empty-record-payment-btn">
                        <Plus className="w-4 h-4 mr-2" /> Record Payment
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : visibleExpenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Search className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                    <p className="font-medium text-slate-700">No payments match your filters</p>
                    <p className="text-sm text-slate-500">Try a different search or category.</p>
                  </TableCell>
                </TableRow>
              ) : visibleExpenses.map((expense) => (
                <TableRow key={expense.id} data-testid={`expense-row-${expense.id}`} className="transition-colors hover:bg-slate-50">
                  <TableCell className="font-mono text-sm">{formatDate(expense.date)}</TableCell>
                  <TableCell>
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 text-purple-700">
                      {expense.category}
                    </span>
                  </TableCell>
                  <TableCell>{expense.description}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${expense.payment_mode === 'cash' ? 'bg-slate-100 text-slate-600' : 'bg-blue-100 text-blue-700'}`}>
                      {expense.payment_mode === 'cash' ? <Wallet className="h-3 w-3" /> : <CreditCard className="h-3 w-3" />}
                      <span className="capitalize">{expense.payment_mode}</span>
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono font-medium text-rose-600">
                    ₹{expense.amount.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-center">
                    {(canEdit('payments') || canDelete('payments')) && (
                      <div className="flex justify-center gap-1">
                        {canEdit('payments') && (
                          <Button variant="ghost" size="icon" onClick={() => handleEditClick(expense)} className="h-7 w-7" data-testid={`edit-expense-${expense.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canDelete('payments') && (
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(expense.id)} className="h-7 w-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-expense-${expense.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
